import { ApiError } from '@/api/http';
import type { MailApi } from '@/api/contract';
import type { SendInput, SendResult } from '@/api/types';
import {
  claimOutbox,
  completeOutbox,
  deferOutbox,
  failOutbox,
  loadOutbox,
  nextDue,
  outboxSnapshot,
  recordStagedIds,
  recoverOutbox,
} from './store';
import type { OutboxFailure, OutboxMessage } from './types';
import { holdUpdates } from '@/pwa/busy';
import { isSendRateLimited, outboxRateLimitedText } from '@/features/send-undo/sendProblem';

/**
 * Flushing the Outbox.
 *
 * ## One at a time, in order
 *
 * The queue is drained oldest first, one message per pass, awaiting each send
 * before the next is claimed. Not for correctness — the idempotency key covers
 * that — but because a mail host is a shared resource with rate limits, and
 * because a user who queued three replies to the same thread expects them to
 * arrive in the order they wrote them.
 *
 * ## Why it cannot send twice
 *
 * Three layers, each of which would be enough on a good day and none of which
 * is enough on its own:
 *
 * 1. **Leader election** (`realtime/leader.ts`) means one tab runs the loop.
 *    It is explicitly best-effort — its own comment accepts two leaders for a
 *    round — so it is a contention reducer, not a guarantee.
 * 2. **The IndexedDB claim** (`claimOutbox`) is a compare-and-set inside a
 *    single `readwrite` transaction, which the browser serialises across every
 *    tab of the origin. Two leaders race; exactly one wins the lease.
 * 3. **The idempotency key**, minted when the message was queued and stored
 *    with it. This is the layer that covers the case the first two cannot: a
 *    request that reached the server and whose answer never came back. The
 *    retry presents the same key and the backend replays the first answer
 *    (ADR 0014, `internal/httpapi/idempotency.go`, 24-hour TTL).
 *
 * ## Attachments
 *
 * The bytes are staged the first time a message is attempted, and the returned
 * ids are written back onto the record before the send is issued. Every later
 * attempt reuses them, so the request body is byte-identical and the
 * idempotency key still fingerprints the same. Re-staging a message that
 * already has ids would change the body and earn `409 idempotency_key_reused`
 * — which is why `replaceOutbox` (edit) drops the ids *and* mints a new key
 * together, never one without the other.
 */

/**
 * Told about each message that went, with the server's answer: a sent message
 * can still carry a caveat (refused recipients, a copy to Sent that is being
 * retried, #84) that nobody would otherwise hear about, because the composer
 * that queued it closed long ago.
 */
export type OnSent = (record: OutboxMessage, result: SendResult) => void;

/** What a single attempt resolved to. */
export type FlushOutcome = 'sent' | 'deferred' | 'failed' | 'idle' | 'skipped';

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : String(error);
}

/**
 * Retryable or not.
 *
 * The default is **retryable**, deliberately. An unrecognised failure that is
 * really permanent costs a few pointless retries and then sits in the Outbox
 * where the user can see it; an unrecognised failure wrongly called permanent
 * puts a perfectly sendable message in the failed pile. Only the statuses that
 * mean "the server understood you and said no" are terminal.
 */
export function isRetryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.isOffline) return true;
  if (error.isServiceUnavailable || error.isRateLimited) return true;
  if (error.idempotencyInProgress) return true;
  // 401 is "you are signed out", not "this message is bad". The message waits
  // for the user to sign in again rather than being marked failed (doc 06 §6).
  if (error.status === 401 || error.status === 408) return true;
  if (error.status >= 500) return true;
  return false;
}

function failureOf(error: unknown, now: number): OutboxFailure {
  if (error instanceof ApiError) {
    return {
      message: error.isRateLimited ? outboxRateLimitedText(error) : error.message,
      code: String(error.code),
      status: error.status,
      at: now,
    };
  }
  return { message: messageOf(error), at: now };
}

/**
 * An attachment that is neither bytes nor a live staged id. Terminal: retrying
 * cannot conjure the file back, so the message stops where the user can see it.
 */
export class MissingAttachmentError extends Error {
  constructor(filename: string) {
    super(
      `${filename} is no longer available to send. Open the message in your Outbox, ` +
        'attach the file again, and send it.',
    );
    this.name = 'MissingAttachmentError';
  }
}

/**
 * Uploads any attachment that has no staged id yet, and remembers the ids.
 *
 * The ids are written back to the record **before** the send is issued, so a
 * retry re-sends a byte-identical body and the pinned idempotency key still
 * fingerprints the same request.
 */
async function stageAttachments(api: MailApi, record: OutboxMessage): Promise<string[]> {
  const ids = record.attachments.map((one) => one.stagedId);
  if (ids.every((id) => id !== undefined)) return ids as string[];

  const staged: (string | undefined)[] = [...ids];
  for (const [index, attachment] of record.attachments.entries()) {
    if (staged[index] !== undefined) continue;
    if (attachment.blob === undefined) throw new MissingAttachmentError(attachment.filename);
    const file = new File([attachment.blob], attachment.filename, {
      type: attachment.contentType,
    });
    const result = await api.compose.uploadAttachment(record.accountId, file);
    staged[index] = result.id;
  }
  await recordStagedIds(record.id, staged);
  return staged as string[];
}

function sendInputFor(record: OutboxMessage, attachmentIds: string[]): SendInput {
  return {
    fromAddress: record.fromAddress,
    to: record.to,
    cc: record.cc,
    bcc: record.bcc,
    subject: record.subject,
    bodyHtml: record.bodyHtml,
    bodyText: record.bodyText,
    attachmentIds,
    ...(record.inReplyToMessageId ? { inReplyToMessageId: record.inReplyToMessageId } : {}),
    ...(record.forwardedMessageId ? { forwardedMessageId: record.forwardedMessageId } : {}),
    ...(record.nextcloudUploadIds?.length ? { nextcloudUploadIds: record.nextcloudUploadIds } : {}),
    mode: record.mode,
    ...(record.draftId ? { draftId: record.draftId } : {}),
  };
}

/**
 * Sends one message, if one is due and this tab can claim it.
 *
 * Returns what happened, so the caller can decide whether to come straight
 * back for the next one.
 */
export async function flushOne(
  api: MailApi,
  options: { now?: () => number; onSent?: OnSent } = {},
): Promise<FlushOutcome> {
  const now = options.now ?? Date.now;
  await loadOutbox();
  const due = nextDue(now(), outboxSnapshot());
  if (due === undefined) return 'idle';

  const record = await claimOutbox(due.id, now());
  if (record === undefined) return 'skipped';

  // A send on the wire must not be cut short by a silent update reload
  // (src/pwa/updates.ts); held only while there is one, so an empty Outbox
  // checked at launch holds nothing.
  const release = holdUpdates('outbox');
  try {
    return await sendClaimed(api, record, now, options.onSent);
  } finally {
    release();
  }
}

async function sendClaimed(
  api: MailApi,
  record: OutboxMessage,
  now: () => number,
  onSent: OnSent | undefined,
): Promise<FlushOutcome> {
  try {
    const attachmentIds = await stageAttachments(api, record);
    const result = await api.compose.sendDirect(
      record.accountId,
      sendInputFor(record, attachmentIds),
      { idempotencyKey: record.idempotencyKey },
    );
    if (result.accepted.length === 0) {
      // Every recipient refused. Retrying cannot change that, and the user has
      // to edit the addresses — so it stops here, named, rather than looping.
      await failOutbox(
        record.id,
        {
          message:
            result.rejected.length > 0
              ? 'No recipient accepted this message.'
              : 'The mail server accepted no recipients for this message.',
          rejected: result.rejected.map((one) => ({
            address: one.address,
            message: one.message,
          })),
          at: now(),
        },
        now(),
      );
      return 'failed';
    }
    await completeOutbox(record.id);
    onSent?.(record, result);
    return 'sent';
  } catch (error) {
    const failure = failureOf(error, now());
    if (isRetryable(error)) {
      // Over the mailbox's hourly send rate (#83) the server says when it
      // lifts; trying again before then only earns the same 429.
      const notBefore =
        isSendRateLimited(error) && error.retryAfter !== undefined ? error.retryAfter * 1000 : undefined;
      await deferOutbox(record.id, failure, now(), notBefore);
      return 'deferred';
    }
    await failOutbox(record.id, failure, now());
    return 'failed';
  }
}

/**
 * Drains the queue, oldest first, stopping at the first message that is not
 * ready to go. Returns how many were sent.
 */
export async function flushOutbox(
  api: MailApi,
  options: {
    now?: () => number;
    onSent?: OnSent;
    /** Checked between messages: a tab that lost the leadership stops here. */
    shouldContinue?: () => boolean;
  } = {},
): Promise<number> {
  await recoverOutbox(options.now?.() ?? Date.now());
  let sent = 0;
  for (;;) {
    if (options.shouldContinue?.() === false) return sent;
    const outcome = await flushOne(api, options);
    if (outcome === 'sent') {
      sent += 1;
      continue;
    }
    // `deferred` and `failed` both leave the message where the user can see
    // it; carrying on to the next one is right, but a `skipped` means another
    // tab holds the lease and an `idle` means there is nothing due.
    if (outcome === 'deferred' || outcome === 'failed') continue;
    return sent;
  }
}
